import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { jobsApi } from '../api/jobs';
import { BackgroundJob, JobType } from '../types';

export interface UseClientJobsResult {
  jobs: BackgroundJob[];
  hasActiveJobs: boolean;
  isLoading: boolean;
  error: string | null;
  isJobTypeRunning: (type: JobType) => boolean;
  getJobByType: (type: JobType) => BackgroundJob | undefined;
}

const ACTIVE_POLL_INTERVAL = 1000; // Fast polling when jobs are active
const IDLE_POLL_INTERVAL = 5000; // Slower polling when no jobs

// Custom event to trigger immediate job refresh across all hook instances
export const JOBS_CHANGED_EVENT = 'jobs-changed';
export const triggerJobsRefresh = () => {
  window.dispatchEvent(new CustomEvent(JOBS_CHANGED_EVENT));
};

/**
 * Hook to poll and track active jobs (pending/running) for a specific client
 */
export const useClientJobs = (clientId: string | null | undefined): UseClientJobsResult => {
  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pollIntervalRef = useRef(ACTIVE_POLL_INTERVAL);
  const timeoutRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchJobs = useCallback(async (id: string): Promise<BackgroundJob[] | null> => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      // Fetch pending and running jobs for this client
      const [pendingResponse, runningResponse] = await Promise.all([
        jobsApi.listJobs({ client_id: id, status: 'pending' }),
        jobsApi.listJobs({ client_id: id, status: 'running' }),
      ]);

      const activeJobs = [...pendingResponse.items, ...runningResponse.items];
      setJobs(activeJobs);
      setError(null);
      return activeJobs;
    } catch (err: unknown) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'CanceledError') {
        return null;
      }
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to fetch jobs';
      setError(errorMessage);
      return null;
    }
  }, []);

  const scheduleNextPoll = useCallback((id: string) => {
    timeoutRef.current = window.setTimeout(async () => {
      const fetchedJobs = await fetchJobs(id);

      if (fetchedJobs !== null) {
        // Use faster polling when jobs are active, slower when idle
        pollIntervalRef.current = fetchedJobs.length > 0 ? ACTIVE_POLL_INTERVAL : IDLE_POLL_INTERVAL;
        scheduleNextPoll(id);
      }
    }, pollIntervalRef.current);
  }, [fetchJobs]);

  useEffect(() => {
    // Reset state when clientId changes
    if (!clientId) {
      setJobs([]);
      setIsLoading(false);
      setError(null);
      pollIntervalRef.current = ACTIVE_POLL_INTERVAL;
      return;
    }

    // Start polling
    setIsLoading(true);
    pollIntervalRef.current = ACTIVE_POLL_INTERVAL;

    const startPolling = async () => {
      await fetchJobs(clientId);
      setIsLoading(false);
      scheduleNextPoll(clientId);
    };

    startPolling();

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [clientId, fetchJobs, scheduleNextPoll]);

  // Listen for jobs-changed event to trigger immediate refresh
  useEffect(() => {
    if (!clientId) return;

    const handleJobsChanged = () => {
      fetchJobs(clientId);
    };

    window.addEventListener(JOBS_CHANGED_EVENT, handleJobsChanged);
    return () => {
      window.removeEventListener(JOBS_CHANGED_EVENT, handleJobsChanged);
    };
  }, [clientId, fetchJobs]);

  const hasActiveJobs = jobs.length > 0;

  const isJobTypeRunning = useCallback(
    (type: JobType): boolean => {
      return jobs.some((job) => job.job_type === type);
    },
    [jobs]
  );

  const getJobByType = useCallback(
    (type: JobType): BackgroundJob | undefined => {
      return jobs.find((job) => job.job_type === type);
    },
    [jobs]
  );

  return useMemo(
    () => ({
      jobs,
      hasActiveJobs,
      isLoading,
      error,
      isJobTypeRunning,
      getJobByType,
    }),
    [jobs, hasActiveJobs, isLoading, error, isJobTypeRunning, getJobByType]
  );
};
